import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { MessageSquareText, Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, buttonVariants, toast } from '@/components/ui'
import { useReplyToConversation } from '@/features/conversations/queries'
import { cn } from '@/lib/utils'

import { useTriageConversationActions } from '../queries'
import { formatAge, priorityVariant } from '../triageFormatting'
import type { TriageConversationItem } from '../types'

const fieldClass =
  'w-full rounded-md border border-border-default bg-background-elevated px-3 py-2 text-sm text-foreground-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60'

interface AwaitingYouCardProps {
  readonly tenantId: string
  readonly tenantSlug: string
  readonly item: TriageConversationItem
}

/**
 * Uma conversa esperando o dono: quem, há quanto tempo, a prioridade POR CÓDIGO com o motivo, o resumo, a necessidade
 * e o rascunho — que só sai por ação explícita (Enviar). A IA nunca envia nada sozinha (ADR-028).
 */
export function AwaitingYouCard({ tenantId, tenantSlug, item }: AwaitingYouCardProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const reply = useReplyToConversation(tenantId, item.conversationSeq)
  const actions = useTriageConversationActions(tenantId)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(item.suggestedReply ?? '')

  const waiting = formatAge(item.unansweredSince, t)
  const hasDraft = item.suggestedReply !== null

  function send(): void {
    const content = draft.trim()
    if (content.length === 0 || reply.isPending) {
      return
    }

    reply.mutate(content, {
      onSuccess: () => {
        toast.success(t('triage.card.sent'))
        void queryClient.invalidateQueries({ queryKey: ['triage', tenantId] })
      },
      onError: () => {
        toast.error(t('triage.card.sendFailed'))
      },
    })
  }

  function dismiss(): void {
    actions.dismiss.mutate(item.conversationSeq, {
      onSuccess: () => {
        toast.success(t('triage.card.dismissed'))
      },
      onError: () => {
        toast.error(t('triage.card.actionFailed'))
      },
    })
  }

  return (
    <article
      className="flex flex-col gap-3 rounded-lg border border-border-default bg-background-elevated p-4"
      data-priority={item.priority ?? 'none'}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="inline-flex items-center gap-1.5 truncate text-sm font-semibold text-foreground-primary">
            {item.contactIsImportant ? (
              <Star
                aria-label={t('triage.card.importantContact')}
                className="h-3.5 w-3.5 fill-status-warning text-status-warning"
              />
            ) : null}
            {item.contactName ?? t('desk.list.unknownContact')}
          </span>
          {waiting !== null ? (
            <span className="text-xs text-foreground-muted">
              {t('triage.card.waitingFor', { time: waiting })}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {item.priority !== null ? (
            <Badge
              variant={priorityVariant(item.priority)}
              title={item.priorityReason ?? undefined}
            >
              {item.priority}
            </Badge>
          ) : (
            <Badge variant="neutral">
              {item.triageExcluded ? t('triage.card.excluded') : t('triage.card.notTriaged')}
            </Badge>
          )}
        </div>
      </header>

      {item.summary !== null ? (
        <p className="text-sm text-foreground-primary">{item.summary}</p>
      ) : (
        <p className="text-sm text-foreground-muted">
          {item.lastMessagePreview ?? t('triage.card.noSummary')}
        </p>
      )}

      {item.need !== null ? (
        <p className="text-sm text-foreground-secondary">
          <span className="font-medium">{t('triage.card.need')}: </span>
          {item.need}
        </p>
      ) : null}

      {item.priorityReason !== null ? (
        <p className="text-xs text-foreground-muted">{item.priorityReason}</p>
      ) : null}

      {hasDraft ? (
        <div className="flex flex-col gap-2 rounded-md border border-status-info/30 bg-status-info-subtle/40 p-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-status-info">
            {t('triage.card.draft')}
          </span>
          {editing ? (
            <textarea
              aria-label={t('triage.card.editDraft')}
              className={cn(fieldClass, 'min-h-24')}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value)
              }}
            />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm text-foreground-primary">
              {draft}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={reply.isPending}
              disabled={draft.trim().length === 0}
              onClick={send}
            >
              {t('triage.card.send')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEditing((value) => !value)
              }}
            >
              {editing ? t('triage.card.doneEditing') : t('triage.card.edit')}
            </Button>
            <Button size="sm" variant="ghost" loading={actions.dismiss.isPending} onClick={dismiss}>
              {t('triage.card.dismiss')}
            </Button>
          </div>
        </div>
      ) : null}

      <footer className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/app/$tenantSlug/desk"
          params={{ tenantSlug }}
          search={{ conversation: item.conversationSeq }}
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <MessageSquareText aria-hidden="true" className="h-4 w-4" />
          {t('triage.card.openConversation')}
        </Link>
      </footer>
    </article>
  )
}
